import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  HeartIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  PauseIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const MassLikes = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [scrapedData, setScrapedData] = useState({ tags: [], sessions: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    accountIds: [],
    recipientSource: 'scraped',
    manualRecipients: '',
    scrapedDataTag: '',
    settings: {
      delayMin: 40,
      delayMax: 80,
      likesPerBatch: 20,
      batchPauseDuration: 600,
      restInterval: 600,
      spamPauseDuration: 3600,
      maxRequestsPerDay: 200
    }
  });

  const { socket } = useSocket();

  // Track which dropdowns are open
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  
  const handleToggleDropdown = useCallback((taskId) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  // Batch socket updates
  const updateQueueRef = useRef(new Map());
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    loadTasks();
    loadAccounts();
    loadScrapedData();
    loadGroups();
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.emit('join-room', 'mass-likes-page');

    const flushUpdates = () => {
      if (updateQueueRef.current.size === 0) return;
      
      setTasks(prevTasks => {
        const updated = [...prevTasks];
        let changed = false;
        
        updateQueueRef.current.forEach((update, taskId) => {
          const index = updated.findIndex(t => t._id === taskId);
          if (index >= 0) {
            updated[index] = {
              ...updated[index],
              ...update,
              progress: {
                ...updated[index].progress,
                ...(update.progress || {})
              },
              ...(update.accountStatus && { accountStatus: update.accountStatus })
            };
            changed = true;
          }
        });
        
        updateQueueRef.current.clear();
        return changed ? updated : prevTasks;
      });
      
      updateTimeoutRef.current = null;
    };

    socket.on('mass-like-progress', (data) => {
      const existing = updateQueueRef.current.get(data.taskId) || {};
      updateQueueRef.current.set(data.taskId, {
        ...existing,
        progress: {
          ...existing.progress,
          ...data.progress
        },
        ...(data.accountStatus && { accountStatus: data.accountStatus }),
        ...(data.status && { status: data.status })
      });
      
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(flushUpdates, 300);
    });

    socket.on('mass-like-task-updated', (data) => {
      setTasks(prevTasks => {
        const index = prevTasks.findIndex(t => t._id === data.taskId);
        if (index >= 0) {
          const updated = [...prevTasks];
          updated[index] = {
            ...updated[index],
            ...(data.status && { status: data.status }),
            ...(data.progress && { progress: { ...updated[index].progress, ...data.progress } }),
            ...(data.accountStatus && { accountStatus: data.accountStatus })
          };
          return updated;
        }
        loadTasks();
        return prevTasks;
      });
    });

    return () => {
      socket.emit('leave-room', 'mass-likes-page');
      socket.off('mass-like-progress');
      socket.off('mass-like-task-updated');
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [socket]);

  const loadTasks = async () => {
    try {
      const response = await api.get('/mass-like/tasks');
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
      if (error.response?.status >= 500) {
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await api.get('/mass-like/accounts');
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/accounts/groups');
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleSelectAllAccounts = () => {
    const selectableAccounts = accounts.filter(acc => 
      acc.hasValidSession === true && acc.inUse !== true
    );
    
    const allSelected = selectableAccounts.length > 0 && 
      selectableAccounts.every(acc => formData.accountIds.includes(acc._id));
    
    if (allSelected) {
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => 
          !selectableAccounts.map(acc => acc._id).includes(id)
        )
      });
    } else {
      const newIds = [...new Set([
        ...formData.accountIds,
        ...selectableAccounts.map(acc => acc._id)
      ])];
      setFormData({ ...formData, accountIds: newIds });
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const groupAccounts = accounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      return acc.hasValidSession === true && acc.inUse !== true;
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => formData.accountIds.includes(id));
    
    if (allGroupSelected) {
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => !groupAccountIds.includes(id))
      });
    } else {
      setFormData({
        ...formData,
        accountIds: [...new Set([...formData.accountIds, ...groupAccountIds])]
      });
    }
  };

  const loadScrapedData = async () => {
    try {
      const response = await api.get('/mass-like/scraped-data');
      setScrapedData(response.data);
    } catch (error) {
      console.error('Error loading scraped data:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      let payload = {
        name: formData.name,
        accountIds: formData.accountIds,
        recipientSource: formData.recipientSource,
        settings: formData.settings
      };

      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }
        payload.manualRecipients = recipients;
      } else {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
      }

      await api.post('/mass-like/tasks', payload);
      toast.success('Task created successfully');
      setShowCreateModal(false);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      if (error.response?.status === 403) {
        setShowPermissionModal(true);
      } else {
        toast.error(error.response?.data?.error || 'Failed to create task');
      }
    }
  };

  const handleEditTask = (task) => {
    setFormData({
      name: task.name,
      accountIds: task.accountIds.map(acc => acc._id || acc),
      recipientSource: task.recipientSource,
      manualRecipients: task.manualRecipients ? task.manualRecipients.join('\n') : '',
      scrapedDataTag: task.scrapedDataTag || '',
      settings: task.settings || {
        delayMin: 5,
        delayMax: 15,
        likesPerBatch: 20,
        batchPauseDuration: 300,
        restInterval: 600,
        spamPauseDuration: 86400,
        maxRequestsPerDay: null
      }
    });
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleUpdateTask = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      let payload = {
        name: formData.name,
        accountIds: formData.accountIds,
        recipientSource: formData.recipientSource,
        settings: formData.settings
      };

      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }
        payload.manualRecipients = recipients;
      } else {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
      }

      const wasPaused = editingTask?.status === 'paused';
      
      await api.put(`/mass-like/tasks/${editingTask._id}`, payload);
      toast.success('Task updated successfully');
      
      // If task was paused, resume it automatically
      if (wasPaused) {
        try {
          await api.post(`/mass-like/tasks/${editingTask._id}/start`);
          toast.success('Task resumed from where it was paused');
        } catch (error) {
          console.error('Error resuming task:', error);
          toast.error('Task updated but failed to resume. Please start it manually.');
        }
      }
      
      setShowEditModal(false);
      setEditingTask(null);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.error || 'Failed to update task');
    }
  };

  const handleStartTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'running' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-like/tasks/${taskId}/start`);
      toast.success('Task started');
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(error.response?.data?.error || 'Failed to start task');
      loadTasks();
    }
  };

  const handlePauseTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'paused' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-like/tasks/${taskId}/stop`);
      toast.success('Task paused');
    } catch (error) {
      console.error('Error pausing task:', error);
      toast.error(error.response?.data?.error || 'Failed to pause task');
      loadTasks();
    }
  };

  const handleStopTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'stopped' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-like/tasks/${taskId}/stop`);
      toast.success('Task stopped');
      loadTasks();
    } catch (error) {
      console.error('Error stopping task:', error);
      toast.error(error.response?.data?.error || 'Failed to stop task');
      loadTasks();
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await api.delete(`/mass-like/tasks/${taskId}`);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.error || 'Failed to delete task');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      accountIds: [],
      recipientSource: 'scraped',
      manualRecipients: '',
      scrapedDataTag: '',
      settings: {
        delayMin: 5,
        delayMax: 15,
        likesPerBatch: 20,
        batchPauseDuration: 300,
        maxLikesPerDay: 500
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'stopped':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getProgressPercentage = (task) => {
    if (!task.progress || task.progress.total === 0) return 0;
    const completed = (task.progress.completed || 0) + (task.progress.failed || 0) + (task.progress.skipped || 0);
    return Math.round((completed / task.progress.total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-rose-950 via-pink-950 to-slate-900' 
          : 'bg-gradient-to-br from-rose-50 via-pink-100 to-rose-100'
      }`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-16 left-1/3 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-rose-600/20' : 'bg-rose-300/40'
          }`} />
          <div className={`absolute -bottom-20 -right-16 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-pink-600/15' : 'bg-pink-300/30'
          }`} />
        </div>
        
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30' 
                  : 'bg-gradient-to-br from-rose-500 to-pink-500 shadow-lg shadow-rose-400/40'
              }`}>
                <HeartIcon className="w-8 h-8 text-white" />
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Mass Likes
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Like the latest posts from scraped users
                </p>
              </div>
            </div>
            
            <button
              onClick={() => {
                if (user?.role !== 'admin' && !user?.features?.canUseMassLike) {
                  setShowPermissionModal(true);
                  return;
                }
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-rose-600/30"
            >
              <PlusIcon className="w-5 h-5" />
              Create Task
            </button>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {tasks.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <HeartIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No tasks yet</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Create your first mass like task to start liking posts automatically.
              </p>
            </div>
          </div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              onStart={handleStartTask}
              onStop={handleStopTask}
              onPause={handlePauseTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              getStatusColor={getStatusColor}
              getProgressPercentage={getProgressPercentage}
              isDropdownOpen={openDropdowns.has(task._id.toString())}
              onToggleDropdown={() => handleToggleDropdown(task._id.toString())}
            />
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <TaskModal
          title="Create Mass Like Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          scrapedData={scrapedData}
          onSubmit={handleCreateTask}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          submitLabel="Create Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingTask && (
        <TaskModal
          title="Edit Mass Like Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          scrapedData={scrapedData}
          onSubmit={handleUpdateTask}
          onClose={() => {
            setShowEditModal(false);
            setEditingTask(null);
            resetForm();
          }}
          submitLabel="Update Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
        />
      )}

      {/* Permission Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Feature Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have permission to use Mass Likes. Please contact support to enable this feature.
            </p>
            <button
              onClick={() => setShowPermissionModal(false)}
              className="w-full px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Card Component (memoized for performance)
const TaskCard = memo(({ task, onStart, onStop, onPause, onEdit, onDelete, getStatusColor, getProgressPercentage, isDropdownOpen, onToggleDropdown }) => {
  const [showDetails, setShowDetails] = useState(isDropdownOpen);

  useEffect(() => {
    setShowDetails(isDropdownOpen);
  }, [isDropdownOpen]);

  const getStatusBadgeColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    if (status === 'paused_batch') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    if (status === 'rate_limited') return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    if (status === 'daily_limit') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    if (status === 'completed') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    if (status === 'error') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const progress = task.progress || { total: 0, completed: 0, failed: 0, skipped: 0 };
  const pending = progress.total - (progress.completed + progress.failed + progress.skipped);

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {task.name}
              </h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                {task.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <UserGroupIcon className="h-4 w-4" />
                {task.accountIds?.length || 0} account{(task.accountIds?.length || 0) !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <HeartIcon className="h-4 w-4" />
                {task.recipientSource === 'scraped' ? task.scrapedDataTag : 'Manual list'}
              </span>
            </div>
            {task.status === 'running' && task.accountStatus && task.accountStatus.length > 0 && (
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  {task.accountStatus.length} total
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3 text-green-600" />
                  {task.accountStatus.filter(acc => acc.status === 'active').length} active
                </span>
                {task.accountStatus.filter(acc => acc.status === 'paused_batch').length > 0 && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-purple-600" />
                    {task.accountStatus.filter(acc => acc.status === 'paused_batch').length} cooldown
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'daily_limit').length > 0 && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-yellow-600" />
                    {task.accountStatus.filter(acc => acc.status === 'daily_limit').length} daily limit
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'rate_limited').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-red-600" />
                    {task.accountStatus.filter(acc => acc.status === 'rate_limited').length} rate limited
                  </span>
                )}
                {/* Global Account.status counters - captcha, auth_expired, suspended */}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha' || acc.status === 'captcha').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-orange-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha' || acc.status === 'captcha').length} captcha
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired' || acc.status === 'auth_expired').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-orange-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired' || acc.status === 'auth_expired').length} auth expired
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended' || acc.status === 'suspended').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-red-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended' || acc.status === 'suspended').length} suspended
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'completed').length > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3 text-blue-600" />
                    {task.accountStatus.filter(acc => acc.status === 'completed').length} completed
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {task.status === 'running' ? (
              <>
                <button
                  onClick={() => onPause(task._id)}
                  className="p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-colors"
                  title="Pause task"
                >
                  <PauseIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onStop(task._id)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Stop task"
                >
                  <StopIcon className="h-5 w-5" />
                </button>
              </>
            ) : task.status === 'paused' || task.status === 'failed' ? (
              <>
                <button
                  onClick={() => onStart(task._id)}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                  title={task.status === 'failed' ? 'Retry task' : 'Resume task'}
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
                {task.status !== 'failed' && (
                  <button
                    onClick={() => onStop(task._id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    title="Stop task"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                )}
              </>
            ) : task.status === 'pending' || task.status === 'stopped' ? (
              <button
                onClick={() => onStart(task._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Start task"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : task.status === 'completed' && pending > 0 ? (
              <button
                onClick={() => onStart(task._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Restart task (continue with pending)"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : null}
            {(task.status === 'pending' || task.status === 'stopped' || task.status === 'paused' || task.status === 'failed') && (
              <button
                onClick={() => onEdit(task)}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                title="Edit task"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => onDelete(task._id)}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              title="Delete task"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {(task.status === 'running' || task.status === 'completed' || task.status === 'paused') && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Progress: {progress.completed + progress.failed + progress.skipped} / {progress.total}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {getProgressPercentage(task)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-rose-600 transition-all duration-300"
                style={{ width: `${getProgressPercentage(task)}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                {progress.completed} sent
              </span>
              <span className="flex items-center gap-1">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                {progress.failed} failed
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-yellow-600" />
                {progress.skipped} skipped
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-gray-400" />
                {pending > 0 ? pending : 0} pending
              </span>
            </div>
          </div>
        )}

        {/* Account Details Section */}
        {task.accountStatus && task.accountStatus.length > 0 && (
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={onToggleDropdown}
              className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span>Account Details</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({task.accountStatus.length} accounts)
                </span>
              </span>
              {showDetails ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>
            
            {showDetails && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {task.accountStatus.map((accStatus) => (
                  <div
                    key={accStatus.accountId?._id || accStatus.accountId}
                    className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-2xl text-sm"
                  >
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      @{accStatus.accountId?.username || 'Unknown'}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeColor(accStatus.status)}`}>
                        {accStatus.status === 'paused_batch' ? 'Cooldown' : 
                         accStatus.status === 'rate_limited' ? 'Rate Limited' :
                         accStatus.status === 'daily_limit' ? 'Daily Limit' :
                         accStatus.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {accStatus.likesSent || 0} likes sent
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Task Modal Component (shared between Create and Edit)
const TaskModal = ({ title, formData, setFormData, accounts, scrapedData, onSubmit, onClose, submitLabel, handleSelectAllAccounts, groups = [], expandedGroups = new Set(), onToggleGroup, onSelectGroup }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Task Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              placeholder="Enter task name"
            />
          </div>

          {/* Accounts Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Accounts *
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formData.accountIds.length} of {accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length} selected
              </span>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
              {accounts.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length > 0 && 
                             accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true)
                               .every(acc => formData.accountIds.includes(acc._id))}
                    onChange={handleSelectAllAccounts}
                    className="rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Available
                  </span>
                </label>
              )}
              {accounts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                  No accounts found. Please add accounts first.
                </p>
              ) : (
                <>
                  {/* Groups Section */}
                  {(() => {
                    const accountGroups = [...new Set(accounts
                      .map(acc => acc.group)
                      .filter(g => g != null && String(g).trim() !== '')
                      .map(g => String(g).trim()))];
                    
                    if (accountGroups.length === 0) return null;
                    
                    return (
                      <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Groups
                        </div>
                        {accountGroups.sort().map((groupName) => {
                          const groupAccounts = accounts.filter(acc => {
                            if (!acc.group) return false;
                            return String(acc.group).trim() === groupName;
                          });
                          const selectableGroupAccounts = groupAccounts.filter(acc => 
                            acc.hasValidSession === true && acc.inUse !== true
                          );
                          const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                          const allGroupSelected = selectableGroupAccounts.length > 0 && 
                            groupAccountIds.every(id => formData.accountIds.includes(id));
                          const isExpanded = expandedGroups.has(groupName);
                          
                          return (
                            <div key={groupName} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center flex-1 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => onToggleGroup && onToggleGroup(groupName)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                    )}
                                  </button>
                                  <input
                                    type="checkbox"
                                    checked={allGroupSelected}
                                    onChange={() => onSelectGroup && onSelectGroup(groupName)}
                                    disabled={selectableGroupAccounts.length === 0}
                                    className={`rounded border-gray-300 text-rose-600 focus:ring-rose-500 ${
                                      selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                  />
                                  <span className={`text-sm font-medium ${
                                    selectableGroupAccounts.length === 0
                                      ? 'text-gray-400 dark:text-gray-500'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                  </span>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="ml-6 space-y-1 mt-1">
                                  {groupAccounts.map((account) => {
                                    const hasValidSession = account.hasValidSession === true;
                                    const inUse = account.inUse === true;
                                    const isDisabled = !hasValidSession || inUse;
                                    return (
                                      <label key={account._id} className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                          <input
                                            type="checkbox"
                                            checked={formData.accountIds.includes(account._id)}
                                            disabled={isDisabled}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setFormData({
                                                  ...formData,
                                                  accountIds: [...formData.accountIds, account._id]
                                                });
                                              } else {
                                                setFormData({
                                                  ...formData,
                                                  accountIds: formData.accountIds.filter(id => id !== account._id)
                                                });
                                              }
                                            }}
                                            className={`rounded border-gray-300 text-rose-600 focus:ring-rose-500 ${
                                              isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                          />
                                          <span className={`ml-2 text-sm ${
                                            isDisabled 
                                              ? 'text-gray-400 dark:text-gray-500' 
                                              : 'text-gray-700 dark:text-gray-300'
                                          }`}>
                                            @{account.username}
                                          </span>
                                        </div>
                                        {!hasValidSession && (
                                          <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                            ⚠️ No Session
                                          </span>
                                        )}
                                        {hasValidSession && inUse && (
                                          <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used">
                                            ⚠️ In Use
                                          </span>
                                        )}
                                        {hasValidSession && !inUse && (
                                          <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session">
                                            ✓ Ready
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Ungrouped Accounts */}
                  {(() => {
                    const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                    if (ungroupedAccounts.length === 0) return null;
                    
                    return (
                      <div className="space-y-1">
                        {(() => {
                          const accountGroups = [...new Set(accounts
                            .map(acc => acc.group)
                            .filter(g => g != null && String(g).trim() !== ''))];
                          if (accountGroups.length > 0) {
                            return (
                              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Ungrouped
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {ungroupedAccounts.map((account) => {
                          const hasValidSession = account.hasValidSession === true;
                          const inUse = account.inUse === true;
                          const isDisabled = !hasValidSession || inUse;
                          return (
                            <label key={account._id} className="flex items-center justify-between">
                              <div className="flex items-center flex-1">
                                <input
                                  type="checkbox"
                                  checked={formData.accountIds.includes(account._id)}
                                  disabled={isDisabled}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        accountIds: [...formData.accountIds, account._id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        accountIds: formData.accountIds.filter(id => id !== account._id)
                                      });
                                    }
                                  }}
                                  className={`rounded border-gray-300 text-rose-600 focus:ring-rose-500 ${
                                    isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span className={`ml-2 text-sm ${
                                  isDisabled 
                                    ? 'text-gray-400 dark:text-gray-500' 
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  @{account.username}
                                </span>
                              </div>
                              {!hasValidSession && (
                                <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                  ⚠️ No Session
                                </span>
                              )}
                              {hasValidSession && inUse && (
                                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used">
                                  ⚠️ In Use
                                </span>
                              )}
                              {hasValidSession && !inUse && (
                                <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session">
                                  ✓ Ready
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Recipient Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipient Source *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="scraped"
                  checked={formData.recipientSource === 'scraped'}
                  onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                  className="mr-2 text-rose-600 focus:ring-rose-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Scraped Data</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="manual"
                  checked={formData.recipientSource === 'manual'}
                  onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                  className="mr-2 text-rose-600 focus:ring-rose-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Manual List</span>
              </label>
            </div>
          </div>

          {/* Scraped Data Selection */}
          {formData.recipientSource === 'scraped' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Scraped Data Tag *
              </label>
              <select
                value={formData.scrapedDataTag}
                onChange={(e) => setFormData({ ...formData, scrapedDataTag: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              >
                <option value="">Select a tag...</option>
                {scrapedData.tags?.map((tag) => (
                  <option key={tag.tag} value={tag.tag}>
                    {tag.tag} ({tag.canDmCount?.toLocaleString() || 0} users)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Manual Recipients */}
          {formData.recipientSource === 'manual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recipients (one per line) *
              </label>
              <textarea
                value={formData.manualRecipients}
                onChange={(e) => setFormData({ ...formData, manualRecipients: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                rows={5}
                placeholder="@username1&#10;@username2&#10;@username3"
              />
            </div>
          )}

          {/* Settings */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Min Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMin: parseInt(e.target.value) || 40 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMax: parseInt(e.target.value) || 80 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Likes per Batch
                </label>
                <input
                  type="number"
                  value={formData.settings.likesPerBatch}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, likesPerBatch: parseInt(e.target.value) || 20 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Batch Pause (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.batchPauseDuration}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, batchPauseDuration: parseInt(e.target.value) || 600 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="60"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Rest Interval (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.restInterval}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, restInterval: parseInt(e.target.value) || 600 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Spam Pause Duration (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.spamPauseDuration}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, spamPauseDuration: parseInt(e.target.value) || 3600 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pause duration when spam is detected (error code 226)</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max Requests Per Day (Per Account)
                </label>
                <input
                  type="number"
                  value={formData.settings.maxRequestsPerDay || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, maxRequestsPerDay: e.target.value ? parseInt(e.target.value) : 200 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-rose-500"
                  min="1"
                  placeholder="Leave empty for unlimited"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Maximum likes per account per day (includes failed attempts). Leave empty for unlimited. Resets every 24 hours.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MassLikes;
